import {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {cn} from '@chekinapp/ui';
import {DocumentPreview} from './DocumentPreview';
import {getDocumentHintKey, getSideCopy} from './documentCopy';
import {PREVIEW_GAP, useFitDocumentPreview} from './fitDocumentPreview';
import type {DocumentSide, DocumentVisual} from './types';
import {getFallbackAspectRatio} from './useDocumentVisualImage';

type Props = {
  visual: DocumentVisual;
  sides?: 'mrz' | 'all';
  twoSides?: boolean;
  withSelfie?: boolean;
  documentValue?: string | null;
  layout?: 'stack' | 'auto';
  className?: string;
};

type PreviewImage = {key: string; side: DocumentSide};

function getPreviewImages(
  visual: DocumentVisual,
  showBothSides: boolean,
): PreviewImage[] {
  if (!showBothSides || !visual.other) return [{key: visual.image, side: visual.side}];
  const front = {
    key: visual.side === 'back' ? visual.other : visual.image,
    side: 'front',
  };
  const back = {key: visual.side === 'back' ? visual.image : visual.other, side: 'back'};
  return [front, back] as PreviewImage[];
}

function getPartsCopy(visual: DocumentVisual, twoSides: boolean) {
  if (visual.kind === 'passport') return 'document_parts_photo_page';
  if (twoSides) return 'document_parts_front_back';
  if (visual.side === 'front') return 'document_parts_front_only';
  return null;
}

function DocumentSidePreview({
  visual,
  sides = 'mrz',
  twoSides = false,
  withSelfie = false,
  documentValue,
  layout = 'stack',
  className,
}: Props) {
  const {t} = useTranslation('identity-verification');
  const hintKey = getDocumentHintKey(documentValue);
  const sideCopy = getSideCopy(visual.side);
  const isAllSides = sides === 'all';
  const images = getPreviewImages(visual, isAllSides && twoSides);
  const partsKey = isAllSides ? getPartsCopy(visual, twoSides) : null;
  const isFitted = layout === 'auto';
  const stageRef = useRef<HTMLDivElement>(null);
  const fit = useFitDocumentPreview(stageRef, {
    aspectRatio: getFallbackAspectRatio(visual.kind),
    count: images.length,
    enabled: isFitted,
  });

  const title = partsKey
    ? t('document_parts_you_will_photograph', {parts: t(partsKey)})
    : t('document_guide_you_will_photograph', {side: t(sideCopy.sideLabel)});
  const hint = partsKey
    ? t(withSelfie ? `${partsKey}_hint_selfie` : `${partsKey}_hint`)
    : t(sideCopy.sideHint);

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center',
        isFitted ? 'min-h-0 flex-1 gap-2.5' : 'gap-4',
        className,
      )}
    >
      <div
        ref={stageRef}
        className={cn(
          'flex w-full items-center justify-center',
          isFitted && 'min-h-[90px] flex-1 py-2.5',
          isFitted && !fit?.sideBySide && 'flex-col',
          !isFitted && 'flex-col gap-4',
        )}
        style={isFitted ? {gap: PREVIEW_GAP} : undefined}
      >
        {images.map(image => (
          <DocumentPreview
            key={image.key}
            visual={visual}
            imageKey={image.key}
            side={image.side}
            className={cn(
              'flex-none',
              isFitted ? (fit ? 'max-w-none' : 'max-w-[380px]') : 'max-w-[300px]',
            )}
            style={isFitted && fit ? {width: fit.imageWidth} : undefined}
          />
        ))}
      </div>
      <div className="flex flex-col items-center gap-0.5 text-center">
        <p
          className={cn(
            'm-0',
            isAllSides
              ? 'text-[13px] font-semibold leading-[18px] text-[var(--chekin-text-primary)]'
              : 'text-[14px] font-bold leading-[1.35] text-[var(--form-title-color,#222222)]',
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            'm-0 text-[var(--chekin-text-secondary)]',
            isAllSides ? 'text-[14px] leading-[17px]' : 'text-[13px] leading-[1.35]',
          )}
        >
          {hint}
        </p>
        {hintKey && (
          <p className="m-0 mt-2 text-[13px] leading-[1.35] text-[var(--chekin-text-primary)]">
            {t(hintKey)}
          </p>
        )}
      </div>
    </div>
  );
}

export {DocumentSidePreview};
